import type { StyleSchema } from '@blocknote/core';
import type { ReactCustomInlineContentRenderProps } from '@blocknote/react';
import * as React from 'react';

import { TOKEN_INLINE_CONFIG, type TokenInlineProps } from './config';
import { EditableDateToken } from './editable-date-token';
import { EditableDropdownToken } from './editable-dropdown-token';
import { EditableMultiSelectToken } from './editable-multi-select-token';
import { EditableTextToken } from './editable-text-token';
import { AutoFilledIndicator } from './auto-filled-indicator';
import { ChangedValueIndicator } from './changed-value-indicator';
import { EMPTY_STATIC_TOKEN_PLACEHOLDER, ReadOnlyToken } from './read-only-token';
import { TokenDebugPopover } from './token-debug-popover';
import { parseTokenValueMetadata } from '../../utils/blocknote-document/value-metadata';

type TokenInlineEditor = {
  isEditable?: boolean;
};

export type TokenInlineRenderProps = ReactCustomInlineContentRenderProps<
  typeof TOKEN_INLINE_CONFIG,
  StyleSchema
>;

/**
 * Renders a single token inline inside the BlockNote editor.
 *
 * Routes on the token's `tokenType` to the matching editable component
 * (`date` → `EditableDateToken`, `dropdown` → `EditableDropdownToken`,
 * `multi_select` → `EditableMultiSelectToken`, and `text`/`textarea`/`number`/`percentage`/`duration`
 * → `EditableTextToken`); falls back to a plain value/label span for unknown types. When the
 * editor is read-only (`editor.isEditable === false`) every type renders via `ReadOnlyToken`
 * instead. Regardless of variant, each token is wrapped in shared "chrome" — the debug popover
 * plus the auto-filled (`*`) and changed-value (`†`) provenance indicators.
 */
export const InlineTokenRenderer: React.FC<TokenInlineRenderProps> = (
  renderProps,
) => {
  const { inlineContent, updateInlineContent } = renderProps;
  const props = inlineContent.props as TokenInlineProps;
  const { tokenType, label } = props;
  const value = props.value ?? '';
  // BlockNote only preserves primitive inline props, so provenance is encoded
  // as JSON and parsed back before rendering the provenance indicators.
  const valueMetadata =
    props.valueMetadata ?? parseTokenValueMetadata(props.valueMetadataJson);

  const editor = (renderProps as unknown as { editor?: TokenInlineEditor })
    .editor;
  const isEditable = editor?.isEditable ?? true;

  const renderWithTokenChrome = (children: React.ReactNode) => (
    <span className="inline-flex items-center align-middle">
      <TokenDebugPopover props={props}>{children}</TokenDebugPopover>
      <AutoFilledIndicator valueMetadata={valueMetadata} />
      <ChangedValueIndicator lastChange={valueMetadata?.lastChange} />
    </span>
  );

  if (!isEditable) {
    // Static-presentation tokens render an em dash for empty values; editable
    // templates keep the default '-' so existing read-mode output is unchanged.
    const isStatic = props.tokenPresentation === 'static_text';
    return renderWithTokenChrome(
      <ReadOnlyToken
        tokenType={tokenType}
        value={value}
        optionsValue={props.options}
        emptyFallback={isStatic ? EMPTY_STATIC_TOKEN_PLACEHOLDER : undefined}
        plain={isStatic}
      />,
    );
  }

  const handleValueChange = React.useCallback(
    (nextValue: string) => {
      updateInlineContent({
        type: 'token',
        props: {
          ...props,
          value: nextValue,
        },
      });
    },
    [updateInlineContent, props],
  );

  if (tokenType === 'date') {
    return renderWithTokenChrome(
      <EditableDateToken
        value={value}
        label={label}
        onValueChange={handleValueChange}
      />,
    );
  }

  if (tokenType === 'dropdown') {
    return renderWithTokenChrome(
      <EditableDropdownToken
        value={value}
        label={label}
        optionsValue={props.options}
        onValueChange={handleValueChange}
      />,
    );
  }

  if (tokenType === 'multi_select') {
    return renderWithTokenChrome(
      <EditableMultiSelectToken
        value={value}
        label={label}
        optionsValue={props.options}
        onValueChange={handleValueChange}
      />,
    );
  }

  if (
    tokenType === 'text' ||
    tokenType === 'textarea' ||
    tokenType === 'number' ||
    tokenType === 'percentage' ||
    tokenType === 'duration'
  ) {
    return renderWithTokenChrome(
      <EditableTextToken
        tokenType={tokenType}
        value={value}
        onValueChange={handleValueChange}
      />,
    );
  }

  return renderWithTokenChrome(<span>{value || label || 'Token'}</span>);
};
